import { Product } from '@/generated/graphql';
import { ShopInfoNode } from '@/lib/types/stoq';
import { deslugify } from '@/lib/utils';
import { determineColorCode } from './colors';
import { parseHandle } from './handles';
import { processProductImages } from './images';
import { determinePreorderSellingPlanInfo } from './preorders';
import {
  determineVariants,
  determineSubmodel1,
  determineSubmodel2,
  determineSubmodelsFromSelectedOptions,
  hasSeatTypeVariants,
} from './variants';
import { ShopifyProductData, VariantMetafield, ProductVideoDetails } from './types';
import { MAKES_WITHOUT_MODELS } from '@/lib/utils/shopify/constants';

export function determineProductType(
  handle: string
): 'Car Covers' | 'Seat Covers' | 'Floor Mats' | 'Unknown' {
  const normalized = handle?.toLowerCase();

  if (normalized?.includes('seat-cover') || normalized?.includes('seat-covers')) {
    return 'Seat Covers';
  } else if (
    normalized?.includes('car-cover') ||
    normalized?.includes('car-covers') ||
    normalized?.includes('suv-cover') ||
    normalized?.includes('suv-covers') ||
    normalized?.includes('truck-cover') ||
    normalized?.includes('truck-covers')
  ) {
    return 'Car Covers';
  } else if (
    normalized?.includes('floor-mat') ||
    normalized?.includes('floor-mats') ||
    normalized?.includes('trunk-mat')
  ) {
    return 'Floor Mats';
  }

  return 'Unknown';
}

export function mapShopifyToModelData(
  shopifyProduct: Product,
  make: string,
  model: string,
  // productVideos: any = {},
  productVideos: ProductVideoDetails,
  displayColor: string,
  typeString: string,
  sellingPlans: ShopInfoNode
): any[] {
  const productType = determineProductType(shopifyProduct?.handle);

  const {
    display_color: extractedDisplayColor,
    year_generation: extractedYearRange,
    cab_size: extractedCabSize,
    model_detail: extractedModelDetail,
    seat_type: extractedSeatType,
    has_seat_type_variants: hasSeatTypeVar,
  } = parseHandle(shopifyProduct?.handle, make, model, typeString);

  const determinedColor = determineColorCode(displayColor || extractedDisplayColor) || displayColor;

  return shopifyProduct?.variants?.edges?.flatMap((variant) => {
    const allocationNode = variant.node?.sellingPlanAllocations?.edges?.[0]?.node;
    const { planIsPreorder, preorderDiscount, expectedInHouseDate } =
      determinePreorderSellingPlanInfo(allocationNode, sellingPlans); // sellingplandata from "Stoq"
    const variables = determineVariants(variant.node.selectedOptions);
    const submodelData = determineSubmodelsFromSelectedOptions(variant.node.selectedOptions);
    //const { isPreorder, preorderDate, preorderDiscount } = determinePreorderStatus(variant.node.metafields);
    const metafields = variant.node.metafields;
    const variantPrice = variant.node.price.amount;
    const variantCompareAtPrice = variant.node.compareAtPrice?.amount;
    const {
      yearRange,
      bodyTrim,
      seatType,
      seatStyle,
      vehicleDetail,
      mirror,
      cabSize,
      bedSize,
      optionsMap,
      modelDetail,
      seatTypeOption,
      seatSubtype,
      seatConfigurations,
    } = variables;

    const productHasSeatTypeVariants =
      hasSeatTypeVar || hasSeatTypeVariants(shopifyProduct?.handle, variant.node.selectedOptions);

    const matchedImages = processProductImages(
      shopifyProduct.images,
      variant,
      shopifyProduct.variants.edges // Pass all variants
    );

    const sku = variant.node.sku as string;
    // const productVideoDetails = productVideos[sku] || {}; // Default to an empty object if SKU is undefined
    const productVideoDetails = productVideos || {};
    const variantImageUrl = variant?.node?.image?.url;
    const variantImages = matchedImages.subImages.map(({ url }) => url);
    const cabSizeFound = cabSize || extractedCabSize;
    const modelDetailFound = modelDetail || extractedModelDetail || '';

    // Use new submodel system - fallback to legacy for backward compatibility
    const determinedSubmodel1 =
      submodelData.submodel1 ||
      determineSubmodel1(productType, bodyTrim, vehicleDetail, cabSizeFound);
    const determinedSubmodel2 =
      submodelData.submodel2 ||
      determineSubmodel2(productType, vehicleDetail, bedSize, cabSizeFound, bodyTrim);
    const determinedSubmodel3 = submodelData.submodel3 || mirror || seatStyle;

    const sellingPlanId =
      variant?.node?.sellingPlanAllocations?.edges.length > 0
        ? variant?.node?.sellingPlanAllocations?.edges[0].node?.sellingPlan?.id
        : '';

    const selectOptionKeys = Array.from(optionsMap.keys());

    const isMakeWithNoModel = MAKES_WITHOUT_MODELS.has(make?.toLowerCase());
    const modelForMap = isMakeWithNoModel
      ? ''
      : deslugify(model) || shopifyProduct.title.split(' ')[1];

    return {
      id: variant.node.id,
      sku,
      parent_generation: yearRange || extractedYearRange, // Parse from title or store elsewhere
      year_generation: yearRange || extractedYearRange,
      make: deslugify(make) || shopifyProduct.vendor,
      model: modelForMap,
      submodel1: determinedSubmodel1,
      submodel2: determinedSubmodel2, // Adjust based on actual submodel structure
      submodel3: determinedSubmodel3,
      feature: variantImageUrl || null,
      product: variantImages.join(','),
      // product: [variantImageUrl],
      product_video_carousel_thumbnail: productVideoDetails.product_video_carousel_thumbnail || '',
      product_video_carousel: productVideoDetails.product_video_carousel || '',
      product_video_zoom: productVideoDetails.product_video_zoom || '',
      product_video_360: productVideoDetails.product_video_360 || '',
      banner: null,
      type: productType, // Assuming a default type
      year_options: yearRange || extractedYearRange,
      make_slug: shopifyProduct.handle.split('-')[0], // Assuming the first part of the handle is the make
      model_slug: shopifyProduct.handle.split('-')[1], // Assuming the second part is the model
      msrp: parseFloat(variantPrice || '0'),
      price: parseFloat(variantCompareAtPrice || '0'),
      quantity: null, // Need separate API call for inventory quantity
      display_color: determinedColor, // Combine both color parts
      display_id: null, // Add logic if available
      display_set: seatType || '',
      'skulabs SKU': null,
      preorder: planIsPreorder,
      preorder_discount: preorderDiscount,
      preorder_date: expectedInHouseDate,
      gtin: variant.node.barcode || null,
      mpn: null, // Add logic if available
      // Shopify Special Properties
      title: shopifyProduct.title,
      productId: shopifyProduct.id,
      variantTitle: variant.node.title,
      handle: shopifyProduct?.handle,
      variant1Name: submodelData.variantNames.variant1Name || (selectOptionKeys?.[0] ?? ''),
      variant2Name: submodelData.variantNames.variant2Name || (selectOptionKeys?.[1] ?? ''),
      variant3Name: submodelData.variantNames.variant3Name || (selectOptionKeys?.[2] ?? ''),
      variant4Name: selectOptionKeys?.[3] ?? '',
      variant5Name: selectOptionKeys?.[4] ?? '',
      model_detail: modelDetailFound ?? '',
      sellingPlanId,
      has_seat_type_variants: productHasSeatTypeVariants,
      seat_type_option: seatTypeOption,
      seat_subtype: seatSubtype,
      seat_configurations: seatConfigurations, // NEW: multi-seat configurations
    };
  });
}

type ProductVideoData = {
  product_video_carousel_thumbnail: string | null;
  product_video_carousel: string | null;
  product_video_zoom: string | null;
  product_video_360: string | null;
};
type ProductDefaultVideo = Record<string, ProductVideoData>;

export function extractProductVideoDetails(
  metafields: VariantMetafield[] | undefined | null,
  sku: string
): ProductVideoDetails {
  const defaultVideos: ProductDefaultVideo = {
    GRBK: {
      product_video_carousel_thumbnail:
        'http://coverland.com/video/thumbnails/Dodge_Charger_GRBK_Thumbnail.webp',
      product_video_carousel:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Dodge%20Charger_GRBK_360_1;1_Small-0JN5mKcyWXYHaCIyeToo1lq52VNzvH.mp4',
      product_video_zoom:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Dodge%20Charger_GRBK_Zoom_Small-pXo6kSJId4R0ZUUob7YANkbJmxoPzr.mp4',
      product_video_360:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Dodge%20Charger_GRBK_360_Small-UHayGqMInQR3oDzwcP8Zj9hSwbRYku.mp4',
    },
    BKGR: {
      product_video_carousel_thumbnail:
        'http://coverland.com/video/thumbnails/Corvette_Thumbnail.webp',
      product_video_carousel:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Corvette%20360%20Video%20Square_Small-bwD5v0jE4pIAAgLQNtdusz69sg1iEN.mp4',
      product_video_zoom:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Corvette%20Zoom_Small-fPwOKZgWphK32vCQZ29bHOO2uZgO5s.mp4 ',
      product_video_360:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Corvette%20360%20Video_Small-4GKndY3dOaAWbteCbEmBly9XNKjcQY.mp4',
    },
    BKRD: {
      product_video_carousel_thumbnail: 'http://coverland.com/video/thumbnails/360_Thumbnail.webp',
      product_video_carousel:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Classic%20Mustang%20360%20Square_Small-yexIolj3fHQx7GAsMmdQ3NxSHq5ixF.mp4',
      product_video_zoom:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Challenger%20Zoom%20Video_Small-a6PwN5MRo4nAHSsKZ5EzlQqwCtkfa3.mp4',
      product_video_360:
        'https://x2kly621zrgfgwll.public.blob.vercel-storage.com/videos/Classic%20Mustang%20360%20Video_Small-Rjog7th2z6XnY9q3lMJ1Yd7Fcwy7H3.mp4',
    },
    EMPTY: {
      product_video_carousel_thumbnail: '',
      product_video_carousel: '',
      product_video_zoom: '',
      product_video_360: '',
    },
  };

  let productVideos: ProductVideoDetails | null = null;
  if (sku?.includes('GRBK')) productVideos = defaultVideos.GRBK;
  else if (sku?.includes('BKGR')) productVideos = defaultVideos.BKGR;
  else if (sku?.includes('BKRD')) productVideos = defaultVideos.BKRD;
  else productVideos = defaultVideos.EMPTY;

  if (!metafields || !Array.isArray(metafields)) {
    return productVideos;
  }

  metafields.forEach((metafield) => {
    if (!metafield) return;

    const { key, value } = metafield;
    if (key in productVideos) {
      (productVideos as any)[key] = value;
    }
  });

  return productVideos;
}
